import React, { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import RecipeDetailModal, { RecipeLike } from './RecipeDetailModal';
import FeedbackButtons from './FeedbackButtons';
import Icon from './Icon';
import Toast from './Toast';
import { useSessionState } from '../utils/useSessionState';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/recipes';

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner'];
const CUISINES = [
  'Any Cuisine', 'American', 'Italian', 'Chinese', 'Mexican', 'Indian',
  'Mediterranean', 'Japanese', 'Thai', 'French',
];

interface QuickMealOption extends RecipeLike {
  id: number;
  recipe_id: string;
  missing_ingredients: string[];
  from_my_recipes?: boolean;
  feedback: string; // '' | 'liked' | 'disliked'
}

const QuickMeal: React.FC = () => {
  const queryClient = useQueryClient();
  // Persisted across navigating away and back (see useSessionState) --
  // otherwise leaving the page and returning lost whatever the last run
  // found, since this was all plain component state before.
  const [mealType, setMealType] = useSessionState('quickMeal.mealType', 'dinner');
  const [cuisine, setCuisine] = useSessionState('quickMeal.cuisine', 'Any Cuisine');
  const [servings, setServings] = useSessionState('quickMeal.servings', '2');
  const [options, setOptions] = useSessionState<QuickMealOption[] | null>('quickMeal.options', null);
  const [selectedRecipe, setSelectedRecipe] = useState<QuickMealOption | null>(null);
  // recipe_id -> My Recipes DB id, for options that are saved (either they
  // came from My Recipes already, or got liked/added-to-shopping-list this
  // session). Quick-meal options always have a DB row now (see backend),
  // so this only tracks the `saved` half of that row.
  const [savedIds, setSavedIds] = useSessionState<Record<string, number>>('quickMeal.savedIds', {});
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const errorDetail = (error: unknown): string | null =>
    axios.isAxiosError(error) ? error.response?.data?.error ?? null : null;

  const generateMutation = useMutation({
    mutationFn: async () => {
      const response = await axios.post(`${API_URL}/quick-meal/`, {
        meal_type: mealType,
        cuisine,
        servings: parseInt(servings, 10) || 1,
      });
      return response.data.options as QuickMealOption[];
    },
    onSuccess: (result) => {
      setOptions(result);
      const initialSaved: Record<string, number> = {};
      result.forEach((option) => {
        if (option.from_my_recipes && option.id) {
          initialSaved[option.recipe_id] = option.id;
        }
      });
      setSavedIds(initialSaved);
    },
  });

  const likeMutation = useMutation({
    mutationFn: async (option: QuickMealOption) => {
      const response = await axios.post(`${API_URL}/my-recipes/like/`, { id: option.id });
      return response.data as { id: number };
    },
    onSuccess: (data, option) => {
      setSavedIds((prev) => ({ ...prev, [option.recipe_id]: data.id }));
      queryClient.invalidateQueries({ queryKey: ['my-recipes'] });
    },
  });

  const unsaveMutation = useMutation({
    mutationFn: async ({ id }: { recipeId: string; id: number }) => {
      await axios.post(`${API_URL}/my-recipes/${id}/unsave/`);
    },
    onSuccess: (_data, { recipeId }) => {
      setSavedIds((prev) => {
        const next = { ...prev };
        delete next[recipeId];
        return next;
      });
      queryClient.invalidateQueries({ queryKey: ['my-recipes'] });
    },
  });

  const toggleLike = (option: QuickMealOption) => {
    const existingId = savedIds[option.recipe_id];
    if (existingId) {
      unsaveMutation.mutate({ recipeId: option.recipe_id, id: existingId });
    } else {
      likeMutation.mutate(option);
    }
  };

  const feedbackMutation = useMutation({
    mutationFn: async ({ option, feedback }: { option: QuickMealOption; feedback: string }) => {
      await axios.post(`${API_URL}/my-recipes/${option.id}/feedback/`, { feedback });
      return feedback;
    },
    onSuccess: (feedback, { option }) => {
      setOptions((prev) => (prev || []).map((o) => (o.recipe_id === option.recipe_id ? { ...o, feedback } : o)));
    },
  });

  const setFeedback = (option: QuickMealOption, value: 'liked' | 'disliked') => {
    feedbackMutation.mutate({ option, feedback: option.feedback === value ? '' : value });
  };

  const addToShoppingListMutation = useMutation({
    mutationFn: async (option: QuickMealOption) => {
      const response = await axios.post(`${API_URL}/my-recipes/${option.id}/add-to-shopping-list/`);
      return response.data as { recipe: { id: number }; added_ingredients: string[] };
    },
    onSuccess: (data, option) => {
      setSavedIds((prev) => ({ ...prev, [option.recipe_id]: data.recipe.id }));
      queryClient.invalidateQueries({ queryKey: ['shopping-list'] });
      queryClient.invalidateQueries({ queryKey: ['my-recipes'] });
      setMessage({
        type: 'success',
        text: data.added_ingredients.length > 0
          ? `Added to My Recipes and put ${data.added_ingredients.length} ingredient${data.added_ingredients.length === 1 ? '' : 's'} on your shopping list.`
          : "Added to My Recipes -- you've already got everything you need!",
      });
    },
    onError: (error) => {
      setMessage({ type: 'error', text: errorDetail(error) || 'Error adding to shopping list.' });
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setOptions(null);
    generateMutation.mutate();
  };

  return (
    <div className="quick-meal">
      <div className="dashboard-header">
        <h1><Icon name="utensils" className="icon" />I'm Hungry Now!</h1>
        <p>Get a few meal ideas for right now, built around what's already in your kitchen.</p>
      </div>

      <form onSubmit={handleSubmit} className="quick-meal-form">
        <div className="quick-meal-fields">
          <div className="form-group">
            <label>Meal</label>
            <select value={mealType} onChange={(e) => setMealType(e.target.value)}>
              {MEAL_TYPES.map((m) => (
                <option key={m} value={m}>{m.charAt(0).toUpperCase() + m.slice(1)}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Cuisine</label>
            <select value={cuisine} onChange={(e) => setCuisine(e.target.value)}>
              {CUISINES.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>Servings</label>
            <input
              type="number"
              min={1}
              max={12}
              value={servings}
              onChange={(e) => setServings(e.target.value)}
            />
          </div>
        </div>

        <button type="submit" className="btn-primary" disabled={generateMutation.isPending}>
          <Icon name={generateMutation.isPending ? 'refresh' : 'search'} className="icon icon-sm" />
          {generateMutation.isPending ? 'Finding meals...' : 'Find Meals'}
        </button>
      </form>

      {generateMutation.isError && (
        <Toast
          type="error"
          text={errorDetail(generateMutation.error) || 'Error generating meal ideas. Please try again.'}
          onDismiss={() => generateMutation.reset()}
        />
      )}

      {generateMutation.isPending && (
        <div className="loading-screen">
          <div className="loading-spinner" />
          <p>Cooking up some ideas&hellip;</p>
        </div>
      )}

      {options && options.length === 0 && !generateMutation.isPending && (
        <div className="empty-state">
          <Icon name="search" className="icon empty-icon" />
          <h2>No Ideas Yet</h2>
          <p>Try a different cuisine or meal type.</p>
        </div>
      )}

      {options && options.length > 0 && (
        <div className="meals-grid">
          {options.map((option) => (
            <div key={option.recipe_id} className="meal-card">
              <div className="meal-header">
                <div className="meal-header-left">
                  <div className="meal-type">
                    <Icon name="flame" className="icon meal-icon" />
                    <span className="meal-label">{option.difficulty}</span>
                  </div>
                  <button
                    className={`like-btn ${savedIds[option.recipe_id] ? 'liked' : ''}`}
                    onClick={() => toggleLike(option)}
                    disabled={likeMutation.isPending || unsaveMutation.isPending}
                    aria-label={savedIds[option.recipe_id] ? 'Remove from My Recipes' : 'Save to My Recipes'}
                  >
                    <Icon name="heart" className="icon" />
                  </button>
                  <FeedbackButtons
                    feedback={option.feedback}
                    onThumbsUp={() => setFeedback(option, 'liked')}
                    onThumbsDown={() => setFeedback(option, 'disliked')}
                    disabled={feedbackMutation.isPending}
                  />
                </div>
                {option.missing_ingredients.length === 0 ? (
                  <span className="meal-status status-completed">Ready to cook</span>
                ) : (
                  <span className="meal-status status-preparing">
                    <Icon name="cart" className="icon icon-sm" /> {option.missing_ingredients.length} needed
                  </span>
                )}
              </div>

              <h3 className="meal-name">{option.name}</h3>
              <div className="meal-meta">
                <span>{option.cuisine}</span>
                <span><Icon name="clock" className="icon icon-sm" /> {option.prep_time + option.cook_time} min</span>
              </div>

              {option.missing_ingredients.length > 0 && (
                <p className="quick-meal-missing">
                  Need: {option.missing_ingredients.join(', ')}
                </p>
              )}

              <div className="meal-actions">
                <button onClick={() => setSelectedRecipe(option)} className="btn-secondary">
                  View Recipe
                </button>
                <button
                  onClick={() => addToShoppingListMutation.mutate(option)}
                  disabled={addToShoppingListMutation.isPending}
                  className="btn-primary"
                >
                  <Icon name="cart" className="icon icon-sm" />
                  I'm Making This
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {selectedRecipe && (
        <RecipeDetailModal recipe={selectedRecipe} onClose={() => setSelectedRecipe(null)} />
      )}

      {message && (
        <Toast type={message.type} text={message.text} onDismiss={() => setMessage(null)} />
      )}
    </div>
  );
};

export default QuickMeal;
